/* eslint-disable import/no-duplicates */
import { useTranslation } from 'react-i18next';
import React from 'react';
import { observer } from 'mobx-react-lite';
import { DeliveryMethod } from '../../../models/delivery-options/common';
import { ReactComponent as AirplaneIcon } from '../../../assets/middle-mile-icons-big/plane-icon-big.svg';
import { ReactComponent as ShipIcon } from '../../../assets/middle-mile-icons-big/ship-icon-big.svg';
import { ReactComponent as RocketIcon } from '../../../assets/middle-mile-icons-big/rocket-icon-big.svg';
import { ReactComponent as CarIcon } from '../../../assets/middle-mile-icons-big/car-icon-big.svg';
import {
  DeliveryMethodTab,
  DeliveryMethodTabContentWrapper,
  MiddleMileCardBottomPart,
  MiddleMileCardTopPart,
  MiddleMileTitle,
  PriceRangeText,
} from './middle-mile-styles';
import { MiddleMileTag } from '../../../features/parcel-creation/parcel-delivery-option/middle-mile-label.component';
import { DeliveryOption } from '../../../models/delivery-options/delivery-option';
import { useStore } from '../../../stores/store';
import { getFormattedPrice } from '../../../utils/price/get-formatted-price';
import { useIsMobile } from '../../../hooks/use-is-mobile.hook';
import { testIds } from '../../../../test-ids';

interface Props {
  deliveryOption: DeliveryOption | undefined;
  isActive: boolean;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
}

interface MiddleMileFilterConfig {
  icon?: JSX.Element;
  translationKey: string;
  testId: string;
}

const config: Record<DeliveryMethod, MiddleMileFilterConfig> = {
  air: {
    icon: <AirplaneIcon />,
    translationKey: 'air',
    testId: testIds.parceCreation.deliveryOptions.middleMile.air,
  },
  sea: {
    icon: <ShipIcon />,
    translationKey: 'sea',
    testId: testIds.parceCreation.deliveryOptions.middleMile.sea,
  },
  car: {
    icon: <CarIcon />,
    translationKey: 'middle_mile_ground',
    testId: testIds.parceCreation.deliveryOptions.middleMile.car,
  },
  expedited: {
    icon: <RocketIcon />,
    translationKey: 'expedited',
    testId: testIds.parceCreation.deliveryOptions.middleMile.expedited,
  },
};

const MiddleMileFilterOption = React.forwardRef<HTMLDivElement, Props>(
  ({ deliveryOption, isActive, onClick }, ref) => {
    const {
      parcelCreationStore: {
        selectedMiddleMileFilter,
        staticDeliveryTimeStrings,
        groupedDeliveryPrices,
      },
      userStore: { getCurrencySymbol },
    } = useStore();

    const isMobile = useIsMobile();

    const { t } = useTranslation();

    if (!deliveryOption || !staticDeliveryTimeStrings || !groupedDeliveryPrices)
      return;

    const deliveryMethod = deliveryOption.middle_mile.delivery_method;
    const staticTimeString = staticDeliveryTimeStrings[deliveryMethod];
    const priceRange = groupedDeliveryPrices[deliveryMethod];

    const { icon, translationKey, testId } =
      config[deliveryOption.middle_mile.delivery_method];

    const currencySymbol = getCurrencySymbol(
      deliveryOption.selectedFirstMile?.currency
    );

    return (
      <DeliveryMethodTab
        ref={ref}
        onClick={onClick}
        $isMiddleMileSelected={!!selectedMiddleMileFilter}
        data-testid={testId}
        $active={isActive}>
        <DeliveryMethodTabContentWrapper
          $isMiddleMileSelected={!!selectedMiddleMileFilter}>
          <MiddleMileCardTopPart>
            {icon}
            <MiddleMileTitle>{t(translationKey)}</MiddleMileTitle>
          </MiddleMileCardTopPart>
          <MiddleMileCardBottomPart>
            <MiddleMileTag
              deliveryMethod={deliveryMethod}
              staticTimeString={staticTimeString}
            />
            <PriceRangeText>
              {getFormattedPrice(priceRange.from, currencySymbol)}
              {priceRange.from !== priceRange.to
                ? ` - ${priceRange.to.toFixed(2)}`
                : ''}
            </PriceRangeText>
          </MiddleMileCardBottomPart>
        </DeliveryMethodTabContentWrapper>
      </DeliveryMethodTab>
    );
  }
);

export default observer(MiddleMileFilterOption);
